import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Building2,
  Mail,
  Phone,
  User,
  TrendingUp,
  Clock,
  MessageSquare,
  Plus,
  CheckCircle2,
  Calendar,
  Edit2,
  Save,
  X,
  PhoneCall,
  Video,
  FileText,
  Home,
  MapPin,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { canWrite } from "@/lib/rbac";
import { trpc } from "@/lib/trpc";
import type { LeadStatus, LeadSource } from "./SalesPipeline";

// ─── Types ───────────────────────────────────────────────────────────────────

type ActivityType = "CALL" | "EMAIL" | "DEMO" | "MEETING" | "NOTE";

// ─── Config ───────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<
  LeadStatus,
  { label: string; variant: "default" | "secondary" | "destructive" | "outline" }
> = {
  PROSPECT: { label: "Prospect", variant: "outline" },
  DEMO: { label: "Démo", variant: "secondary" },
  NEGOCIATION: { label: "Négociation", variant: "default" },
  GAGNE: { label: "Gagné", variant: "default" },
  PERDU: { label: "Perdu", variant: "destructive" },
};

const SOURCE_LABELS: Record<LeadSource, string> = {
  INBOUND: "Entrant",
  OUTBOUND: "Sortant",
  TRAVAUX_BTP: "TravauxBTP",
  REFERRAL: "Référence",
  OTHER: "Autre",
};

const ACTIVITY_CONFIG: Record<ActivityType, { label: string; icon: any; color: string }> = {
  CALL: { label: "Appel", icon: PhoneCall, color: "text-primary" },
  EMAIL: { label: "Email", icon: Mail, color: "text-pipeline-demo" },
  DEMO: { label: "Démo", icon: Video, color: "text-success" },
  MEETING: { label: "RDV", icon: Calendar, color: "text-warning" },
  NOTE: { label: "Note", icon: FileText, color: "text-muted-foreground" },
};

const PIPELINE_STEPS: LeadStatus[] = ["PROSPECT", "DEMO", "NEGOCIATION", "GAGNE", "PERDU"];

// ─── Score visualization ──────────────────────────────────────────────────────

function ScoreGauge({ score }: { score: number }) {
  const color = score >= 80 ? "text-success" : score >= 60 ? "text-warning" : "text-destructive";
  const bgColor = score >= 80 ? "bg-success/10" : score >= 60 ? "bg-warning/10" : "bg-destructive/10";
  const barColor = score >= 80 ? "bg-success" : score >= 60 ? "bg-warning" : "bg-destructive";
  return (
    <div className={`rounded-lg p-4 ${bgColor}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium">Score de qualification</span>
        <span className={`text-2xl font-bold ${color}`}>{score}/100</span>
      </div>
      <div className="h-2 bg-white/60 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${barColor}`} style={{ width: `${score}%` }} />
      </div>
      <div className="text-xs mt-1 text-muted-foreground">
        {score >= 80 ? "Lead chaud — priorité haute" : score >= 60 ? "Lead tiède — à nurturer" : "Lead froid — à qualifier"}
      </div>
    </div>
  );
}

// ─── Pipeline Progress ────────────────────────────────────────────────────────

function PipelineProgress({ current }: { current: LeadStatus }) {
  const activeIndex = PIPELINE_STEPS.filter((s) => s !== "PERDU").indexOf(current);
  const isLost = current === "PERDU";

  return (
    <div className="flex items-center gap-1">
      {PIPELINE_STEPS.filter((s) => s !== "PERDU").map((step, i) => {
        const isActive = step === current;
        const isDone = !isLost && i < activeIndex;
        return (
          <div key={step} className="flex items-center gap-1">
            <div
              className={`text-xs px-2 py-1 rounded-full font-medium ${
                isActive
                  ? "bg-primary text-primary-foreground"
                  : isDone
                  ? "bg-success/10 text-success"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              {STATUS_CONFIG[step].label}
            </div>
            {i < PIPELINE_STEPS.filter((s) => s !== "PERDU").length - 1 && (
              <div className={`h-px w-4 ${isDone ? "bg-success/30" : "bg-muted"}`} />
            )}
          </div>
        );
      })}
      {isLost && (
        <div className="ml-2 text-xs px-2 py-1 rounded-full bg-destructive/10 text-destructive font-medium">
          Perdu
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function SalesLeadDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = canWrite(user?.staffRole);

  const { data: lead, isLoading, refetch } = trpc.sales.leads.getById.useQuery(
    { id: id! },
    { enabled: !!id }
  );

  const { data: sourceProject } = trpc.privateProjects.getById.useQuery(
    { id: lead?.sourcePrivateProjectId ?? "" },
    { enabled: !!lead?.sourcePrivateProjectId }
  );

  const updateMutation = trpc.sales.leads.update.useMutation({
    onSuccess: () => {
      toast.success("Lead mis à jour");
      setEditing(false);
      refetch();
    },
    onError: () => toast.error("Erreur lors de la mise à jour"),
  });

  const addActivityMutation = trpc.sales.leads.addActivity.useMutation({
    onSuccess: () => {
      toast.success("Activité ajoutée");
      setShowActivityDialog(false);
      setActivityForm({ type: "NOTE", notes: "", scheduledAt: "" });
      refetch();
    },
    onError: () => toast.error("Erreur lors de l'ajout"),
  });

  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState<{
    name: string;
    company: string;
    email: string;
    phone: string;
    status: LeadStatus;
    estimatedValue: number;
    notes: string;
  } | null>(null);
  const [showActivityDialog, setShowActivityDialog] = useState(false);

  // Close reason dialog
  const [closeReasonDialogOpen, setCloseReasonDialogOpen] = useState(false);
  const [pendingCloseReason, setPendingCloseReason] = useState("");
  const [pendingCloseNote, setPendingCloseNote] = useState("");
  const [activityForm, setActivityForm] = useState<{ type: ActivityType; notes: string; scheduledAt: string }>({
    type: "NOTE",
    notes: "",
    scheduledAt: "",
  });

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20 text-muted-foreground text-sm">
          Chargement…
        </div>
      </AdminLayout>
    );
  }

  if (!lead) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20 text-muted-foreground text-sm">
          Lead introuvable
        </div>
      </AdminLayout>
    );
  }

  const currentEdit = editForm ?? {
    name: lead.name,
    company: lead.company,
    email: lead.email,
    phone: lead.phone ?? "",
    status: lead.status as LeadStatus,
    estimatedValue: lead.estimatedValue,
    notes: lead.notes ?? "",
  };

  function startEditing() {
    setEditForm({
      name: lead!.name,
      company: lead!.company,
      email: lead!.email,
      phone: lead!.phone ?? "",
      status: lead!.status as LeadStatus,
      estimatedValue: lead!.estimatedValue,
      notes: lead!.notes ?? "",
    });
    setEditing(true);
  }

  function handleSave() {
    if (!editForm) return;
    if (
      (editForm.status === "GAGNE" || editForm.status === "PERDU") &&
      editForm.status !== lead?.status
    ) {
      setPendingCloseReason("");
      setPendingCloseNote("");
      setCloseReasonDialogOpen(true);
      return;
    }
    updateMutation.mutate({ id: id!, ...editForm });
  }

  function handleConfirmClose() {
    if (!editForm) return;
    updateMutation.mutate({
      id: id!,
      ...editForm,
      closeReason: pendingCloseReason || undefined,
      closeNote: pendingCloseNote || undefined,
    });
    setCloseReasonDialogOpen(false);
  }

  function handleAddActivity() {
    if (!activityForm.notes) {
      toast.error("Notes requises");
      return;
    }
    addActivityMutation.mutate({
      leadId: id!,
      type: activityForm.type,
      notes: activityForm.notes,
      scheduledAt: activityForm.scheduledAt || undefined,
    });
  }

  const statusCfg = STATUS_CONFIG[lead.status as LeadStatus];
  const activities = lead.activities ?? [];

  return (
    <AdminLayout>
      {/* Back + header */}
      <div className="flex items-start gap-4 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Retour
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold">{lead.name}</h1>
            <Badge variant={statusCfg?.variant} className="status-badge">{statusCfg?.label}</Badge>
          </div>
          <PipelineProgress current={lead.status as LeadStatus} />
        </div>
        {canEdit && (
          <div className="flex gap-2">
            {editing ? (
              <>
                <Button size="sm" onClick={handleSave} disabled={updateMutation.isPending}>
                  <Save className="h-3.5 w-3.5 mr-1" />
                  {updateMutation.isPending ? "Enregistrement…" : "Enregistrer"}
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setEditing(false); setEditForm(null); }}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              </>
            ) : (
              <Button size="sm" variant="outline" onClick={startEditing}>
                <Edit2 className="h-3.5 w-3.5 mr-1" />
                Modifier
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Left column: info + score */}
        <div className="col-span-1 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <User className="h-4 w-4" />
                Informations
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {editing ? (
                <>
                  <div>
                    <Label className="text-xs">Nom</Label>
                    <Input value={currentEdit.name} onChange={(e) => setEditForm((f) => ({ ...f!, name: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-xs">Entreprise</Label>
                    <Input value={currentEdit.company} onChange={(e) => setEditForm((f) => ({ ...f!, company: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-xs">Email</Label>
                    <Input value={currentEdit.email} onChange={(e) => setEditForm((f) => ({ ...f!, email: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-xs">Téléphone</Label>
                    <Input value={currentEdit.phone} onChange={(e) => setEditForm((f) => ({ ...f!, phone: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-xs">Statut</Label>
                    <Select value={currentEdit.status} onValueChange={(v) => setEditForm((f) => ({ ...f!, status: v as LeadStatus }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Valeur estimée (€/an)</Label>
                    <Input type="number" value={currentEdit.estimatedValue} onChange={(e) => setEditForm((f) => ({ ...f!, estimatedValue: Number(e.target.value) }))} />
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-sm">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span>{lead.company}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <a href={`mailto:${lead.email}`} className="text-primary hover:underline">{lead.email}</a>
                  </div>
                  {lead.phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <a href={`tel:${lead.phone}`} className="hover:underline">{lead.phone}</a>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Assigné à</span>
                    <span>{lead.assignee || "—"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{lead.estimatedValue.toLocaleString("fr-FR")} €/an</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Source:</span>
                    <span>{SOURCE_LABELS[lead.source as LeadSource] || lead.source}</span>
                  </div>
                  <div className="pt-2 border-t text-xs text-muted-foreground">
                    Créé le {new Date(lead.createdAt).toLocaleDateString("fr-FR")}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Score card */}
          <Card>
            <CardContent className="pt-4">
              <ScoreGauge score={lead.score} />
            </CardContent>
          </Card>

          {/* TravauxBTP source project card */}
          {lead.sourcePrivateProjectId && (
            <Card className="border-primary/30 bg-primary/5">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2 text-primary">
                  <Home className="h-4 w-4" />
                  Projet TravauxBTP source
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {sourceProject ? (
                  <>
                    <p className="text-sm font-medium leading-snug">{sourceProject.title}</p>
                    {(sourceProject.budgetMin || sourceProject.budgetMax) && (
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        <TrendingUp className="h-3.5 w-3.5 shrink-0" />
                        <span>
                          {sourceProject.budgetMin && sourceProject.budgetMax
                            ? `${sourceProject.budgetMin.toLocaleString("fr-FR")} – ${sourceProject.budgetMax.toLocaleString("fr-FR")} €`
                            : sourceProject.budgetMin
                            ? `≥ ${sourceProject.budgetMin.toLocaleString("fr-FR")} €`
                            : `≤ ${sourceProject.budgetMax!.toLocaleString("fr-FR")} €`}
                        </span>
                      </div>
                    )}
                    {(sourceProject.city || sourceProject.department) && (
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        <MapPin className="h-3.5 w-3.5 shrink-0" />
                        <span>
                          {[sourceProject.city, sourceProject.department].filter(Boolean).join(", ")}
                        </span>
                      </div>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full mt-1 text-xs"
                      onClick={() => navigate(`/private-projects?id=${lead.sourcePrivateProjectId}`)}
                    >
                      <ExternalLink className="h-3 w-3 mr-1" />
                      Voir le projet
                    </Button>
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground">Chargement…</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Notes */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Notes</CardTitle>
            </CardHeader>
            <CardContent>
              {editing ? (
                <Textarea
                  rows={4}
                  value={currentEdit.notes}
                  onChange={(e) => setEditForm((f) => ({ ...f!, notes: e.target.value }))}
                />
              ) : (
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {lead.notes || "Aucune note"}
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right column: activity timeline */}
        <div className="col-span-2">
          <Card className="h-full">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Historique des interactions
              </CardTitle>
              {canEdit && (
                <Button size="sm" onClick={() => setShowActivityDialog(true)}>
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Ajouter
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {activities.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    Aucune interaction enregistrée
                  </p>
                )}
                {activities.map((act) => {
                  const cfg = ACTIVITY_CONFIG[act.type as ActivityType];
                  if (!cfg) return null;
                  const Icon = cfg.icon;
                  return (
                    <div key={act.id} className="flex gap-3">
                      <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted ${cfg.color}`}>
                        <Icon aria-hidden="true" className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-xs font-medium">{cfg.label}</span>
                          <span className="text-xs text-muted-foreground">par {act.author}</span>
                          <span className="text-xs text-muted-foreground ml-auto">
                            {new Date(act.createdAt).toLocaleDateString("fr-FR", {
                              day: "2-digit",
                              month: "short",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">{act.notes}</p>
                        {act.completedAt && (
                          <div className="flex items-center gap-1 mt-1 text-xs text-success">
                            <CheckCircle2 className="h-3 w-3" />
                            Complété
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Add activity dialog */}
      <Dialog open={showActivityDialog} onOpenChange={setShowActivityDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nouvelle interaction</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Type</Label>
              <Select
                value={activityForm.type}
                onValueChange={(v) => setActivityForm((f) => ({ ...f, type: v as ActivityType }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ACTIVITY_CONFIG).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Notes *</Label>
              <Textarea
                placeholder="Résumé de l'interaction..."
                rows={4}
                value={activityForm.notes}
                onChange={(e) => setActivityForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </div>
            <div>
              <Label className="text-xs">Date planifiée (optionnel)</Label>
              <Input
                type="datetime-local"
                value={activityForm.scheduledAt}
                onChange={(e) => setActivityForm((f) => ({ ...f, scheduledAt: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActivityDialog(false)}>Annuler</Button>
            <Button onClick={handleAddActivity} disabled={addActivityMutation.isPending}>
              {addActivityMutation.isPending ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close reason dialog */}
      <Dialog open={closeReasonDialogOpen} onOpenChange={setCloseReasonDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editForm?.status === "GAGNE" ? "Raison du gain" : "Raison de la perte"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Raison principale <span className="text-muted-foreground text-xs">(optionnel)</span></Label>
              <Select value={pendingCloseReason} onValueChange={setPendingCloseReason}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner une raison..." />
                </SelectTrigger>
                <SelectContent>
                  {editForm?.status === "PERDU" ? (
                    <>
                      <SelectItem value="Trop cher">Trop cher</SelectItem>
                      <SelectItem value="Concurrent retenu">Concurrent retenu</SelectItem>
                      <SelectItem value="Timing non adapté">Timing non adapté</SelectItem>
                      <SelectItem value="Scope non aligné">Scope non aligné</SelectItem>
                      <SelectItem value="Sans réponse">Sans réponse</SelectItem>
                      <SelectItem value="Autre">Autre</SelectItem>
                    </>
                  ) : (
                    <>
                      <SelectItem value="Fonctionnalités">Fonctionnalités</SelectItem>
                      <SelectItem value="Prix compétitif">Prix compétitif</SelectItem>
                      <SelectItem value="Recommandation">Recommandation</SelectItem>
                      <SelectItem value="Réactivité">Réactivité</SelectItem>
                      <SelectItem value="Autre">Autre</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Note complémentaire <span className="text-muted-foreground text-xs">(optionnel)</span></Label>
              <Textarea
                placeholder="Détails supplémentaires..."
                value={pendingCloseNote}
                onChange={(e) => setPendingCloseNote(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCloseReasonDialogOpen(false);
                updateMutation.mutate({ id: id!, ...editForm! });
              }}
            >
              Passer
            </Button>
            <Button onClick={handleConfirmClose} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? "Enregistrement…" : "Confirmer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
